import { Injectable, OnInit } from '@angular/core';
import { NameFilter, Project } from '../models/project.model';
import { ReplaySubject, BehaviorSubject } from 'rxjs';
import { PricingPlanMapped } from '../models/subscription-plan.model';
import { MappedAddOn } from '../models/stripe-product-addons.model';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private dbName = 'OpsIncDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private fiveMinTimeout: number | null = null;
  private timeoutFinished$ = new ReplaySubject<void>(1);
  // projectStatuses: MappedObject = {};

  reportGroupId: string = '';

  // Synchronous in-memory cache
  private cache = {
    currentProject: null as Project | null,
    allProjects: [] as Project[],
    allProjectsTs: 0,
    stripeProducts: [] as PricingPlanMapped[],
    stripeAddOns: [] as MappedAddOn[],
    userSubscriptions: [] as any[],
    currentCompany: null as NameFilter | null,
    usersEditedByList: [] as Array<{ id: string; fullName: string }>,
    calculatedHistory: false,
    bankAccounts: [] as any[],
    fiscalYears: [] as any[],
  };

  private initialized = new BehaviorSubject<boolean>(false);
  public initialized$ = this.initialized.asObservable();


  constructor() {
    this.initializeDB().then(() => {
      this.loadAllData().then(() => {
        this.initialized.next(true);
      });
    });
  }

  /* -------------------- Initialization Methods -------------------- */
  private async initializeDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        //console.error('IndexedDB error:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBRequest).result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBRequest).result;

        if (!db.objectStoreNames.contains('projects')) {
          const projectsStore = db.createObjectStore('projects', {
            keyPath: '_id',
          });
          projectsStore.createIndex('versionLink', 'versionLink', {
            unique: false,
          });
          projectsStore.createIndex('code', 'code', { unique: false });
        }

        db.createObjectStore('currentData', { keyPath: 'key' });
      };
    });
  }

  private async loadAllData(): Promise<void> {
    await Promise.all([
      this.loadCurrentProject(),
      this.loadAllProjects(),
      this.loadStripeProducts(),
      this.loadStripeAddOns(),
      this.loadUserSubscriptions(),
      this.loadCurrentCompany(),
      this.loadFiscalYearsForCompany(),
      this.loadUsersEditedByList(),
      this.loadBankAccountCalculatedHistory(),
    ]);
  }

  /* -------------------- Internal Data Loaders -------------------- */
  private async loadCurrentProject(): Promise<void> {
    const cp = await this.getDataInternal('currentProject');
    this.cache.currentProject = cp ? JSON.parse(cp) : null;
  }

  private async loadAllProjects(): Promise<void> {
    const ts = await this.getDataInternal('allProjectsTs');
    this.cache.allProjectsTs = ts ? parseInt(ts) : 0;

    const dataDiff = new Date().getTime() - this.cache.allProjectsTs;
    const fiveMinFit = dataDiff / 1000 / 60;

    if (fiveMinFit >= 5 && this.cache.allProjectsTs !== 0) {
      this.cache.allProjects = [];
      return;
    }

    try {
      const projects = await this.getAllProjectsInternal();
      this.cache.allProjects = projects;
      // this.projectStatuses = projects.reduce((acc, project) => {
      //   acc[project._id] = project.status || '0';
      //   return acc;
      // }, {} as MappedObject);
    } catch {
      this.cache.allProjects = [];
    }
  }

  private async loadStripeProducts(): Promise<void> {
    const sp = await this.getDataInternal('stripeProducts');
    this.cache.stripeProducts = sp ? JSON.parse(sp) : [];
  }

  private async loadStripeAddOns(): Promise<void> {
    const sao = await this.getDataInternal('stripeAddOns');
    this.cache.stripeAddOns = sao ? JSON.parse(sao) : [];
  }

  private async loadUserSubscriptions(): Promise<void> {
    const aus = await this.getDataInternal('userSubscriptions');
    this.cache.userSubscriptions = aus ? JSON.parse(aus) : [];
  }

  private async loadCurrentCompany(): Promise<void> {
    const cp = await this.getDataInternal('currentCompany');
    this.cache.currentCompany = cp ? JSON.parse(cp) : null;
  }

  private async loadFiscalYearsForCompany(): Promise<void> {
    const fy = await this.getDataInternal('fiscalYears');
    this.cache.fiscalYears = fy ? JSON.parse(fy) : [];
  }


  private async loadUsersEditedByList(): Promise<void> {
    const uebl = await this.getDataInternal('usersEditedByList');
    this.cache.usersEditedByList = uebl ? JSON.parse(uebl) : [];
  }

  private async loadBankAccountCalculatedHistory(): Promise<void> {
    const isCalculated = await this.getDataInternal('calculatedHistory');
    this.cache.calculatedHistory = isCalculated ? true : false;
  }

  /* -------------------- Internal DB Operations -------------------- */
  private async getDataInternal(key: string): Promise<string | null> {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('currentData', 'readonly');
      const store = transaction.objectStore('currentData');
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  private async getAllProjectsInternal(): Promise<Project[]> {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('projects', 'readonly');
      const store = transaction.objectStore('projects');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  private async saveDataInternal(key: string, value: string): Promise<void> {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction('currentData', 'readwrite');
        const store = transaction.objectStore('currentData');
        const request = store.put({ key, value });

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch (e) {
        //console.warn('IndexedDB: The database connection is closing.');
      }
    });
  }

  private async saveProjectsInternal(projects: Project[]): Promise<void> {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('projects', 'readwrite');
      const store = transaction.objectStore('projects');

      // Clear existing projects
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        // Add all new projects
        const putRequests = projects.map((project) => store.put(project));
        Promise.all(putRequests).then(() => resolve());
      };

      clearRequest.onerror = () => reject(clearRequest.error);
    });
  }

  private async updateProjectInternal(project: Project): Promise<void> {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction('projects', 'readwrite');
      const store = transaction.objectStore('projects');

      // Clear existing projects
      const updateRequest = store.put(project);

      updateRequest.onsuccess = () => resolve();

      updateRequest.onerror = () => reject(updateRequest.error);
    });
  }

  /* -------------------- Public Synchronous Methods -------------------- */

  // Timeout Management
  getTimeoutFinishedObservable() {
    return this.timeoutFinished$.asObservable();
  }

  clearFiveMinTimeout() {
    if (this.fiveMinTimeout) {
      clearTimeout(this.fiveMinTimeout);
    }
  }

  clearAndFetchForRecalculation() {
    this.clearEssential();
    this.timeoutFinished$.next();
  }

  clearAllProjectsTs() {
    this.cache.allProjectsTs = 0;
    this.saveDataBackground('allProjectsTs', '');
  }

  setFiveMinTimeout() {
    this.clearFiveMinTimeout();
    this.fiveMinTimeout = setTimeout(() => {
      this.cache.allProjects = [];
      // this.projectStatuses = {};
      this.timeoutFinished$.next();
    }, 300000) as unknown as number;
  }

  // Project Methods
  getCurrentProject(): Project | null {
    return this.cache.currentProject;
  }

  resetFiscalYearsData() {
    this.cache.fiscalYears = [];
    this.saveDataBackground('fiscalYears', '');
  }

  setCurrentProject(project: Project): void {
    this.cache.currentProject = project;
    this.saveDataBackground('currentProject', JSON.stringify(project));
  }

  getAllProjects(cold?: boolean): Project[] {
    const dataAddedTs = this.cache.allProjectsTs || 1003830059502;
    const dataDiff = new Date().getTime() - dataAddedTs;

    const fiveMinFit = dataDiff / 1000 / 60;
    // && !cold
    if (fiveMinFit >= 5) {
      this.clearEssential();
      return [];
    } else {
      const ap = this.cache.allProjects;
      return ap || [];
    }
  }

  setAllProjects(projects: Project[]): void {
    this.cache.allProjects = projects.map((project) => ({
      ...project,
      status: project.status || '0',
    }));

    // this.projectStatuses = projects.reduce((acc, project) => {
    //   acc[project._id] = project.status || '0';
    //   return acc;
    // }, {} as MappedObject);

    this.cache.allProjectsTs = new Date().getTime();

    this.saveDataBackground(
      'allProjectsTs',
      this.cache.allProjectsTs.toString()
    );
    this.saveProjectsBackground(projects);
    this.setFiveMinTimeout();
  }

  getProjectById(id: string): Project | null {
    return this.cache.allProjects.find((p) => p._id === id) || null;
  }

  getProjectByCode(code: string): Project | null {
    return this.cache.allProjects.find((p) => p.code === code) || null;
  }

  getAllProjectsByVersionLink(versionLink: string): Project[] {
    return this.cache.allProjects.filter((p) => p.versionLink === versionLink);
  }

  setProjectById(id: string, project: Project): void {
    const filteredProjects = this.cache.allProjects.filter((p) => p._id !== id);
    filteredProjects.push(project);
    this.cache.allProjects = filteredProjects;
    if (this.cache.currentProject?._id === id) {
      this.setCurrentProject(project);
    }
    this.updateProjectInternal(project);
  }

  updateProjectCache(project: Project): void {
    const filteredProjects = this.cache.allProjects.filter((p) => p._id !== project._id);
    filteredProjects.push(project);
    this.cache.allProjects = filteredProjects;
    this.updateProjectInternal(project);
  }

  deleteProject(id: string): void {
    const filteredProjects = this.cache.allProjects.filter((p) => p._id !== id);
    this.setAllProjects(filteredProjects);
  }

  // getProjectStatus(projectId: string): string {
  //   const projectMatch = this.cache.allProjects.find((projectItem)=> projectItem._id === projectId);
  //   return projectMatch?.status || '0';
  // }

  // updateProjectStatus(versionId: string, status: string): void {
  //   this.projectStatuses[versionId] = status;
  //   const updatedProjects = this.cache.allProjects.map((project) => {
  //     if (project._id === versionId) {
  //       return { ...project, status };
  //     }
  //     return project;
  //   });
  //   this.cache.allProjects = updatedProjects;
  //   this.saveProjectsBackground(updatedProjects);
  // }

  // Stripe Products
  getAllStripeProducts(): PricingPlanMapped[] {
    return this.cache.stripeProducts;
  }

  setAllStripeProducts(stripeProducts: PricingPlanMapped[]): void {
    this.cache.stripeProducts = stripeProducts;
    this.saveDataBackground('stripeProducts', JSON.stringify(stripeProducts));
  }

  // Stripe Add-ons
  getAllStripeAddOns(): MappedAddOn[] {
    return this.cache.stripeAddOns;
  }

  setAllStripeAddOns(stripeAddOns: MappedAddOn[]): void {
    this.cache.stripeAddOns = stripeAddOns;
    this.saveDataBackground('stripeAddOns', JSON.stringify(stripeAddOns));
  }

  // User Subscriptions
  getAllUserSubscriptions(): any[] {
    return this.cache.userSubscriptions;
  }

  getFiscalYearsForCompany(): any[] {
    return this.cache.fiscalYears;
  }

  setFiscalYearsForCompany(fiscalYears: any[]) {
    const mappedFiscalYears = fiscalYears
      .map((fyItem) => {
        return {
          _id: fyItem._id,
          yearNumber: fyItem.yearNumber,
          year: fyItem.year,
        };
      })
      .sort((a: any, b: any) => {
        return b.yearNumber - a.yearNumber;
      });

    this.cache.fiscalYears = mappedFiscalYears;
    this.saveDataBackground('fiscalYears', JSON.stringify(mappedFiscalYears));
  }

  setAllUserSubscriptions(allUserSubs: any[]): void {
    this.cache.userSubscriptions = allUserSubs;
    this.saveDataBackground('userSubscriptions', JSON.stringify(allUserSubs));
  }

  getCurrentCompany() : NameFilter | null {
    return this.cache.currentCompany;
  }
  // Company Methods
  async getCurrentCompanyInRetryLoop(): Promise<NameFilter | null> {
    let attempts = 0;
    const maxAttempts = 10;
    const retryDelay = 100;

    const getCurrentCompanyWithRetry = (resolve: (value: NameFilter | null) => void) => {
      const company = this.cache.currentCompany;
      if (company) {
        resolve(company);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(() => getCurrentCompanyWithRetry(resolve), retryDelay);
      } else {
        resolve(null);
      }
    };

    // If called as a normal function, return synchronously if available, otherwise return null.
    // If called as an async function, return a Promise that resolves when the value is available or after retries.
    if (this.cache.currentCompany) {
      return this.cache.currentCompany;
    } else {
      // @ts-ignore
      return new Promise<NameFilter | null>((resolve) => {
        getCurrentCompanyWithRetry(resolve);
      });
    }
  }

  setCurrentCompany(company: NameFilter): void {
    const currentComp = this.cache.currentCompany;
    this.cache.currentCompany = company;
    this.saveDataBackground('currentCompany', JSON.stringify(company));

    if (currentComp?._id !== company?._id) {
      this.clearAndFetchForRecalculation();
    }
  }

  // User Edited Methods
  getUserEditedById(userId: string): any {
    return (
      this.cache.usersEditedByList.find((user) => user.id === userId) || null
    );
  }

  setUserEditedById(id: string, fullName: string): void {
    const existingUserIndex = this.cache.usersEditedByList.findIndex(
      (user) => user.id === id
    );

    if (existingUserIndex >= 0) {
      this.cache.usersEditedByList[existingUserIndex].fullName = fullName;
    } else {
      this.cache.usersEditedByList.push({ id, fullName });
    }

    this.saveDataBackground(
      'usersEditedByList',
      JSON.stringify(this.cache.usersEditedByList)
    );
  }

  // Bank Account Methods
  getBankAccountCalculatedHistory(): boolean {
    return this.cache.calculatedHistory;
  }

  setBankAccountCalculatedHistory(): void {
    this.cache.calculatedHistory = true;
    this.saveDataBackground('calculatedHistory', 'true');
  }

  clearBankAccountCalculatedHistory(): void {
    this.cache.calculatedHistory = false;
    this.saveDataBackground('calculatedHistory', '');
  }

  // Clear Methods
  async clearEssential(): Promise<void> {
    this.clearFiveMinTimeout();
    this.cache.allProjects = [];
    this.cache.allProjectsTs = 0;
    this.cache.fiscalYears = [];
    this.saveDataBackground('fiscalYears', '');
    this.saveDataBackground('allProjects', '');
    this.saveDataBackground('allProjectsTs', '');
    // TODO SIMKE LOSMI SLOBA
    // Nismo sigurni da li je ovo dodato zbog user-a
    // i da li ima efekta na poslednji selektovan projekat i
    // kompaniju, testirati i proveriti naknadno
    // await this.clearIndexedDB();
  }

  clearData(): void {
    this.clearFiveMinTimeout();
    this.cache = {
      currentProject: null,
      allProjects: [],
      allProjectsTs: 0,
      stripeProducts: [],
      stripeAddOns: [],
      userSubscriptions: [],
      currentCompany: null,
      usersEditedByList: [],
      calculatedHistory: false,
      bankAccounts: [],
      fiscalYears: [],
    };
    this.clearIndexedDB();
    this.clearAllStorage();
  }

  /**
   * Clear all storage including localStorage, sessionStorage, and IndexedDB
   */
  clearAllStorage(): void {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage (preserve some system items)
      const keysToPreserve = ['language', 'theme']; // Add any keys you want to preserve
      const preservedData: { [key: string]: string } = {};
      
      keysToPreserve.forEach(key => {
        const value = sessionStorage.getItem(key);
        if (value) {
          preservedData[key] = value;
        }
      });
      
      sessionStorage.clear();
      
      // Restore preserved data
      Object.keys(preservedData).forEach(key => {
        sessionStorage.setItem(key, preservedData[key]);
      });

      // Clear IndexedDB
      this.clearIndexedDB();

      // Clear any cached data in memory
      this.clearMemoryCache();

      // Clear any service worker caches if they exist
      this.clearServiceWorkerCaches();

      //console.log('All storage cleared successfully');
    } catch (error) {
      //console.error('Error clearing storage:', error);
    }
  }

  /**
   * Clear memory cache
   */
  private clearMemoryCache(): void {
    // Clear any global variables or cached data
    if (typeof window !== 'undefined') {
      // Clear any global cache objects
      (window as any).__CACHE__ = {};
    }
  }

  /**
   * Clear service worker caches
   */
  private async clearServiceWorkerCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        //console.log('Service worker caches cleared');
      }
    } catch (error) {
      //console.error('Error clearing service worker caches:', error);
    }
  }

  deleteStore(): void {
    this.clearFiveMinTimeout();
    this.cache = {
      currentProject: null,
      allProjects: [],
      allProjectsTs: 0,
      stripeProducts: [],
      stripeAddOns: [],
      userSubscriptions: [],
      currentCompany: null,
      usersEditedByList: [],
      calculatedHistory: false,
      bankAccounts: [],
      fiscalYears: [],
    };
    this.db?.deleteObjectStore(this.dbName);
    sessionStorage.clear();
  }

  /* -------------------- Background Operations -------------------- */
  private saveDataBackground(key: string, value: string): void {
    if (!this.db) {
      this.initializeDB().then(() => this.saveDataInternal(key, value));
    } else {
      this.saveDataInternal(key, value).catch((err) => {
        //console.error('Failed to save data:', err);
      });
    }
  }

  private saveProjectsBackground(projects: Project[]): void {
    if (!this.db) {
      this.initializeDB().then(() => this.saveProjectsInternal(projects));
    } else {
      this.saveProjectsInternal(projects).catch((err) => {
        //console.error('Failed to save projects:', err);
      });
    }
  }

  private async clearIndexedDB(): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        ['currentData', 'projects'],
        'readwrite'
      );

      transaction.objectStore('currentData').clear();
      transaction.objectStore('projects').clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}
